/**
 * GhostButton - Transparent button for canvas HUD panels
 * Used in: SingleItemHUD, MarqueeActionDialog, TidyUpDropdown, GenerativeActionsDropdown
 * 
 * Renders as:
 * - Icon only (no children) → square button (--toolbar-inner-height × --toolbar-inner-height)
 * - Label only (no icon) → padded button (0 12px)
 * - Icon + children → flex row with 6px gap
 * 
 * DNA source: BUTTON_STYLE from SingleItemHUD.jsx and silentButtonStyle from MarqueeActionDialog.jsx
 * 
 * @param {ReactNode} icon - Optional icon element
 * @param {ReactNode} children - Optional label text
 * @param {function} onClick - Click handler
 * @param {function} onMouseDown - MouseDown handler (for stopPropagation in HUD contexts)
 * @param {boolean} disabled - Whether button is disabled
 * @param {boolean} destructive - Red text on hover (for delete actions)
 * @param {string} variant - "default" | "rail"
 *   rail is a full-width navigation row: left-aligned, on its own height, with a leading
 *   inset the host surface supplies. It exists because this component styles itself inline,
 *   so a host that wanted a row had to override four declarations with !important. It is
 *   opt-in and changes nothing for any other caller.
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
import { forwardRef } from 'react';
import BaseButton from './base/BaseButton';

const GhostButton = forwardRef(function GhostButton({
  icon,
  children,
  onClick,
  onMouseDown,
  disabled = false,
  destructive = false,
  variant = 'default',
  className = '',
  style = {},
  type = 'button',
  ...buttonProps
}, ref) {
  const isIconOnly = icon && !children;
  const isLabelOnly = !icon && children;
  const isRail = variant === 'rail';

  const baseStyle = {
    // Sizing: square for icon-only, padded for label/combo
    width: isIconOnly ? 'var(--ui-ghost-button-height)' : isRail ? '100%' : undefined,
    height: isRail ? 'var(--ui-rail-button-height)' : 'var(--ui-ghost-button-height)',
    padding: isIconOnly
      ? '0'
      : isRail
        ? `0 var(--ui-rail-button-padding-inline-end) 0 var(--ui-rail-button-padding-inline-start)`
        : `0 var(--ui-ghost-button-padding-inline)`,
    // Layout
    borderRadius: 'var(--ui-ghost-button-radius)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: isRail ? 'flex-start' : 'center',
    gap: isLabelOnly ? undefined : 'var(--space-xs)',
    // Appearance
    backgroundColor: 'transparent',
    color: disabled
      ? 'var(--text-disabled)'
      : destructive
        ? 'var(--ui-ghost-button-destructive-text)'
        : 'var(--ui-ghost-button-text)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    // Typography (for icon+label mode)
    fontSize: 'var(--ui-ghost-button-font-size)',
    fontWeight: 'var(--ui-ghost-button-font-weight)',
    flexShrink: 0,
    // Merge overrides
    ...style
  };

  return (
    <BaseButton
      ref={ref}
      type={type}
      onClick={onClick}
      onMouseDown={onMouseDown}
      disabled={disabled}
      className={`ghost-btn${isRail ? ' ghost-btn--rail' : ''} ${className}`}
      data-destructive={destructive}
      style={baseStyle}
      {...buttonProps}
    >
      {icon}
      {children}
    </BaseButton>
  );
});

export default GhostButton;
